import React, { useEffect, useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { get } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import KpiCard from '../components/widgets/KpiCard';
import SectionCard from '../components/widgets/SectionCard';
import EntriesDrillDown from '../components/EntriesDrillDown';
import { fmtBRL, fmtBRLCompact, fmtPct, competenceShort } from '../lib/format';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  DollarSign, TrendingUp, Percent, PieChart, Briefcase, Building2,
  ArrowDown, ArrowUp, Layers, Activity, Upload, Info,
  Receipt, Wallet, Coins, Gauge,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart as RPieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
} from 'recharts';

const COLORS = ['#D4AF37', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const TooltipDark = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-md p-3 shadow-xl text-[12px]">
      <div className="text-zinc-400 mb-1.5 font-medium">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.color }} />
          <span className="text-zinc-300">{p.name}:</span>
          <span className="font-mono text-zinc-50">{formatter ? formatter(p.value) : fmtBRL(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { competence, companyFilter, companies, periodMode, competenceFrom, competenceTo } = useApp();
  const [kpis, setKpis] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [topExpenses, setTopExpenses] = useState([]);
  const [topCC, setTopCC] = useState([]);
  const [byProject, setByProject] = useState([]);
  const [contractsByPlan, setContractsByPlan] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detectar se filtro é Lícito.Guru (para exibir gráfico por plano)
  const selectedCompany = companies.find((c) => c.id === companyFilter);
  const isLicitoSelected = selectedCompany && (selectedCompany.name || '').toLowerCase().includes('lícito');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cid = companyFilter === 'all' ? null : companyFilter;
        // Params de competência conforme modo
        const periodParams = periodMode === 'range'
          ? { competence_from: competenceFrom, competence_to: competenceTo }
          : { competence };
        // Endpoints ainda de mês fixo usam `competence` — quando em range, usam o competence_to como mês de referência
        const singleCompetence = periodMode === 'range' ? competenceTo : competence;
        const [k, ev, te, tc, bp] = await Promise.all([
          get('/dashboard/kpis', periodParams),
          get('/dashboard/evolution', { months: 12, ...(cid ? { company_id: cid } : {}) }),
          get('/dashboard/top-expenses', { competence: singleCompetence, limit: 8, ...(cid ? { company_id: cid } : {}) }),
          get('/dashboard/top-cost-centers', { competence: singleCompetence, limit: 8, ...(cid ? { company_id: cid } : {}) }),
          get('/dashboard/by-project', { competence: singleCompetence, ...(cid ? { company_id: cid } : {}) }),
        ]);
        setKpis(k);
        setEvolution(ev);
        setTopExpenses(te);
        setTopCC(tc);
        setByProject(bp);
        // Se Lícito selecionada, carregar contracts/by-plan
        if (cid && (companies.find((c) => c.id === cid)?.name || '').toLowerCase().includes('lícito')) {
          const bpl = await get('/contracts/by-plan', { company_id: cid });
          setContractsByPlan(Array.isArray(bpl) ? bpl : []);
        } else {
          setContractsByPlan([]);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [competence, companyFilter, companies, periodMode, competenceFrom, competenceTo]);

  // Filtrar dados por empresa selecionada para os KPI cards
  const filtered = useMemo(() => {
    if (!kpis) return null;
    const build = (src) => {
      const receita = Number(src.receita || 0);
      const custos = Number(src.custos || 0);
      const despesas = Number(src.despesas || 0);
      const impostos = Number(src.impostos || 0);
      const investimentos = Number(src.investimentos || 0);
      const lucroLiquidoReal = receita - custos - despesas - impostos - investimentos;
      const receitaLiquida = receita - impostos;
      // Lucro Líquido Operacional = Receita Líquida − Custos − Despesas
      // (equivale a: Receita − Impostos − Custos − Despesas; NÃO deduz investimentos)
      const lucroLiquidoOperacional = receita - impostos - custos - despesas;
      const margemLiquida = receita ? (lucroLiquidoReal / receita) * 100 : 0;
      const pct = (v) => (receita ? (v / receita) * 100 : 0);
      return {
        receita,
        impostos,
        receita_liquida: receitaLiquida,
        custos,
        despesas,
        lucro_liquido_operacional: lucroLiquidoOperacional,
        investimentos,
        lucro_liquido_real: lucroLiquidoReal,
        // manter chaves legadas para compatibilidade com o resto do arquivo
        lucro_liquido: lucroLiquidoReal,
        margem: margemLiquida,
        ebitda: receita - custos - despesas,
        // Percentuais sobre a Receita Total (Item B2 do PDF)
        pct: {
          receita: 100,
          impostos: pct(impostos),
          receita_liquida: pct(receitaLiquida),
          custos: pct(custos),
          despesas: pct(despesas),
          lucro_liquido_operacional: pct(lucroLiquidoOperacional),
          investimentos: pct(investimentos),
          lucro_liquido_real: pct(lucroLiquidoReal),
        },
      };
    };
    if (companyFilter === 'all') {
      const base = build(kpis.consolidated);
      base.proporcional = kpis.proportional?.lucro_liquido || 0;
      base.receita_proporcional = kpis.proportional?.receita || 0;
      return base;
    }
    const c = kpis.by_company.find((x) => x.company_id === companyFilter);
    if (!c) return build({ receita: 0, custos: 0, despesas: 0, impostos: 0, investimentos: 0 });
    const base = build(c);
    base.proporcional = c.lucro_proporcional;
    base.receita_proporcional = c.receita * (c.ownership_pct / 100);
    return base;
  }, [kpis, companyFilter]);

  const sparkData = evolution.map((e) => e.receita);
  const lucroSpark = evolution.map((e) => e.lucro);
  const hasData = !!kpis && (kpis.by_company || []).length > 0;
  const [drillType, setDrillType] = useState(null);

  return (
    <div className="fade-in">
      <TopBar
        title="Dashboard Executivo"
        subtitle="Visão consolidada do desempenho financeiro do Grupo CEAP BRASIL"
      />

      <div className="px-8 py-7 space-y-6">
        {!hasData && !loading && (
          <div className="flex items-center gap-4 px-5 py-4 bg-amber-950/20 border border-amber-700/40 rounded-md" data-testid="dashboard-empty-banner">
            <Info size={18} className="text-amber-400 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-100">Sem dados nesta competência</div>
              <div className="text-[12px] text-amber-100/70 mt-0.5">
                Importe a planilha de Fluxo de Caixa de cada empresa para ver os números reais do grupo. Os KPIs e gráficos abaixo se preenchem automaticamente após o primeiro upload.
              </div>
            </div>
            <Link to="/importar-fluxo-caixa">
              <Button className="btn-gold gap-1.5" size="sm" data-testid="btn-dashboard-import">
                <Upload size={13} /> Importar Planilha
              </Button>
            </Link>
          </div>
        )}
        {/* KPI Grid — 9 cards na ordem pedida (Doc de Alteração):
             Receita Total → Impostos → Receita Líquida → Custos → Despesas →
             Lucro Líquido Operacional → Investimentos → Lucro Líquido Real → Margem Líquida.
             Cada card exibe % em relação à Receita Total. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <KpiCard
            label="Receita Total"
            value={filtered?.receita || 0}
            sparkline={sparkData}
            accent="#D4AF37"
            icon={DollarSign}
            testid="kpi-receita-total"
            highlight
            onClick={() => setDrillType('RECEITA')}
          />
          <KpiCard
            label="Impostos"
            value={filtered?.impostos || 0}
            accent="#8B5CF6"
            icon={Briefcase}
            testid="kpi-impostos"
            pctOfRevenue={filtered?.pct?.impostos}
            onClick={() => setDrillType('IMPOSTO')}
          />
          <KpiCard
            label="Receita Líquida"
            value={filtered?.receita_liquida || 0}
            accent="#D4AF37"
            icon={Receipt}
            testid="kpi-receita-liquida"
            pctOfRevenue={filtered?.pct?.receita_liquida}
          />
          <KpiCard
            label="Custos"
            value={filtered?.custos || 0}
            accent="#EF4444"
            icon={ArrowDown}
            testid="kpi-custos"
            pctOfRevenue={filtered?.pct?.custos}
            onClick={() => setDrillType('CUSTO')}
          />
          <KpiCard
            label="Despesas"
            value={filtered?.despesas || 0}
            accent="#EF4444"
            icon={Layers}
            testid="kpi-despesas"
            pctOfRevenue={filtered?.pct?.despesas}
            onClick={() => setDrillType('DESPESA')}
          />
          <KpiCard
            label="Lucro Líquido Operacional"
            value={filtered?.lucro_liquido_operacional || 0}
            accent={(filtered?.lucro_liquido_operacional || 0) >= 0 ? '#10B981' : '#EF4444'}
            icon={Activity}
            testid="kpi-lucro-operacional"
            pctOfRevenue={filtered?.pct?.lucro_liquido_operacional}
          />
          <KpiCard
            label="Investimentos"
            value={filtered?.investimentos || 0}
            accent="#3B82F6"
            icon={ArrowUp}
            testid="kpi-investimentos"
            pctOfRevenue={filtered?.pct?.investimentos}
            onClick={() => setDrillType('INVESTIMENTO')}
          />
          <KpiCard
            label="Lucro Líquido Real"
            value={filtered?.lucro_liquido_real || 0}
            sparkline={lucroSpark}
            accent={(filtered?.lucro_liquido_real || 0) >= 0 ? '#10B981' : '#EF4444'}
            icon={TrendingUp}
            testid="kpi-lucro-liquido"
            pctOfRevenue={filtered?.pct?.lucro_liquido_real}
          />
          <KpiCard
            label="Margem Líquida"
            value={filtered?.margem || 0}
            format="percent"
            accent="#3B82F6"
            icon={Gauge}
            testid="kpi-margem"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard
            title="Evolução Financeira"
            subtitle="Últimos meses - Receita vs Lucro"
            testid="chart-evolution"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evolution} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradReceita" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradLucro" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="competence" tickFormatter={competenceShort} stroke="#52525B" fontSize={11} tickLine={false} />
                  <YAxis stroke="#52525B" fontSize={11} tickFormatter={fmtBRLCompact} tickLine={false} axisLine={false} />
                  <Tooltip content={<TooltipDark />} labelFormatter={competenceShort} />
                  <Area type="monotone" dataKey="receita" name="Receita" stroke="#D4AF37" strokeWidth={2} fill="url(#gradReceita)" />
                  <Area type="monotone" dataKey="lucro" name="Lucro" stroke="#10B981" strokeWidth={2} fill="url(#gradLucro)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <SectionCard
            title="Receita por Empresa"
            subtitle="Participação no período"
            testid="chart-revenue-by-company"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RPieChart>
                  <Pie
                    data={(kpis?.by_company || []).filter((c) => c.receita > 0)}
                    dataKey="receita"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {(kpis?.by_company || []).map((c, i) => (
                      <Cell key={i} fill={c.color || COLORS[i % COLORS.length]} stroke="#09090B" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip content={<TooltipDark />} />
                </RPieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
              {(kpis?.by_company || []).slice(0, 6).map((c, i) => (
                <div key={c.company_id} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2 h-2 rounded-full" style={{ background: c.color || COLORS[i % COLORS.length] }} />
                  <span className="text-zinc-400 flex-1 truncate">{c.name}</span>
                  <span className="font-mono text-zinc-300">{fmtBRLCompact(c.receita)}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Lucro por Empresa"
            subtitle="Resultado líquido + proporcional ao grupo"
            testid="chart-profit-by-company"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kpis?.by_company || []} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#52525B" fontSize={10} tickFormatter={fmtBRLCompact} />
                  <YAxis type="category" dataKey="name" stroke="#A1A1AA" fontSize={10} width={110} tickLine={false} axisLine={false} />
                  <Tooltip content={<TooltipDark />} />
                  <Bar dataKey="lucro_liquido" name="Lucro Líquido" radius={[0, 4, 4, 0]}>
                    {(kpis?.by_company || []).map((c, i) => (
                      <Cell key={i} fill={c.lucro_liquido >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                  <Bar dataKey="lucro_proporcional" name="Proporcional Grupo" radius={[0, 4, 4, 0]} fill="#D4AF37" fillOpacity={0.6} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
        </div>

        {/* Lícito.Guru - Distribuição de Contratos por Plano (Personalizado × Premium) */}
        {isLicitoSelected && contractsByPlan.length > 0 && (
          <SectionCard
            title="Lícito.Guru — Receita Recorrente por Plano"
            subtitle="Distribuição de contratos ativos entre Personalizado e Premium"
            testid="chart-licito-plans"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RPieChart>
                    <Pie
                      data={contractsByPlan}
                      dataKey="mrr"
                      nameKey="plan"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      label={(d) => d.plan}
                      labelLine={false}
                    >
                      {contractsByPlan.map((p, i) => (
                        <Cell
                          key={i}
                          fill={p.plan === 'PREMIUM' ? '#D4AF37' : p.plan === 'PERSONALIZADO' ? '#3B82F6' : '#71717A'}
                          stroke="#09090B"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipDark />} />
                  </RPieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {contractsByPlan.map((p) => (
                  <div key={p.plan} className="border border-zinc-800 rounded-md p-4 bg-zinc-900/30" data-testid={`plan-card-${p.plan}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ background: p.plan === 'PREMIUM' ? '#D4AF37' : p.plan === 'PERSONALIZADO' ? '#3B82F6' : '#71717A' }} />
                        <span className="text-zinc-100 font-medium">{p.plan === 'SEM_PLANO' ? 'Sem Plano' : p.plan}</span>
                      </div>
                      <span className="font-mono text-sm text-zinc-100">{p.count} contrato{p.count === 1 ? '' : 's'}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="overline text-zinc-500 text-[10px]">MRR</div>
                        <div className="font-mono gold-text">{fmtBRL(p.mrr)}</div>
                      </div>
                      <div>
                        <div className="overline text-zinc-500 text-[10px]">Total Contratado</div>
                        <div className="font-mono text-zinc-300 text-sm">{fmtBRLCompact(p.total)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Top Despesas e Custos" subtitle="Maiores saídas do período" testid="chart-top-expenses">
            {topExpenses.length === 0 ? (
              <div className="py-8 text-center text-zinc-500 text-sm">Sem dados</div>
            ) : (
              <div className="space-y-2.5">
                {topExpenses.map((e, i) => {
                  const max = topExpenses[0]?.amount || 1;
                  const pct = (e.amount / max) * 100;
                  return (
                    <div key={i} className="space-y-1" data-testid={`top-expense-${i}`}>
                      <div className="flex justify-between items-center text-[12px]">
                        <span className="text-zinc-300 truncate flex-1 mr-3">{e.description}</span>
                        <span className="font-mono text-zinc-50">{fmtBRL(e.amount)}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, #EF4444 0%, #B91C1C 100%)`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Ranking de Fontes de Receita" subtitle="Receita por fonte/unidade" testid="chart-by-project">
            {byProject.length === 0 ? (
              <div className="py-8 text-center text-zinc-500 text-sm">Sem dados</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                {byProject.slice(0, 12).map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 py-2 border-b border-zinc-800/60 last:border-0"
                    data-testid={`project-row-${i}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="overline text-zinc-600 w-5 text-right">#{i + 1}</span>
                      <span className="text-[12px] text-zinc-300 truncate">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] font-mono">
                      <span className="text-zinc-500">Rec.</span>
                      <span className="gold-text">{fmtBRLCompact(p.receita)}</span>
                      <span className={`${p.resultado >= 0 ? 'emerald-text' : 'red-text'} w-20 text-right`}>
                        {fmtBRLCompact(p.resultado)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* Ranking Tabela com participações */}
        <SectionCard
          title="Desempenho por Empresa - Consolidado x Proporcional"
          subtitle="Compreenda o impacto real de cada empresa no resultado do grupo"
          padded={false}
          testid="company-table"
        >
          <div className="overflow-x-auto">
            <table className="w-full ceap-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th className="text-right">Participação</th>
                  <th className="text-right">Receita</th>
                  <th className="text-right">Lucro Líquido</th>
                  <th className="text-right">Margem</th>
                  <th className="text-right">Lucro p/ Grupo</th>
                </tr>
              </thead>
              <tbody>
                {(kpis?.by_company || []).map((c) => (
                  <tr key={c.company_id} data-testid={`row-company-${c.company_id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-7 rounded-sm" style={{ background: c.color }} />
                        <span className="text-zinc-100 font-medium">{c.name}</span>
                      </div>
                    </td>
                    <td className="text-right font-mono text-zinc-300">{c.ownership_pct.toFixed(2)}%</td>
                    <td className="text-right font-mono text-zinc-100">{fmtBRL(c.receita)}</td>
                    <td className={`text-right font-mono ${c.lucro_liquido >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {fmtBRL(c.lucro_liquido)}
                    </td>
                    <td className="text-right font-mono text-zinc-300">{fmtPct(c.margem)}</td>
                    <td className={`text-right font-mono font-semibold ${c.lucro_proporcional >= 0 ? 'gold-text' : 'red-text'}`}>
                      {fmtBRL(c.lucro_proporcional)}
                    </td>
                  </tr>
                ))}
                {kpis && (
                  <tr className="bg-zinc-900/40 font-semibold">
                    <td className="text-zinc-50">CONSOLIDADO</td>
                    <td className="text-right text-zinc-500 font-mono">—</td>
                    <td className="text-right font-mono gold-text">{fmtBRL(kpis.consolidated.receita)}</td>
                    <td className={`text-right font-mono ${kpis.consolidated.lucro_liquido >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {fmtBRL(kpis.consolidated.lucro_liquido)}
                    </td>
                    <td className="text-right font-mono text-zinc-300">{fmtPct(kpis.consolidated.margem)}</td>
                    <td className={`text-right font-mono ${(kpis.proportional?.lucro_liquido || 0) >= 0 ? 'gold-text' : 'red-text'}`}>
                      {fmtBRL(kpis.proportional?.lucro_liquido || 0)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Centros de custo */}
        <SectionCard title="Top Centros de Custo" subtitle="Centros com maior movimentação no período" testid="chart-cost-centers">
          {topCC.length === 0 ? (
            <div className="py-8 text-center text-zinc-500 text-sm">Sem dados</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {topCC.map((cc, i) => (
                <div key={i} className="border border-zinc-800 rounded-md p-4 bg-zinc-900/30" data-testid={`cc-card-${i}`}>
                  <div className="overline text-zinc-500 truncate">{cc.name}</div>
                  <div className="mt-2 text-xl font-mono font-semibold text-zinc-50">{fmtBRLCompact(cc.despesa)}</div>
                  <div className="mt-2 flex items-center justify-between text-[11px]">
                    <span className="text-zinc-500">Receita</span>
                    <span className="font-mono gold-text">{fmtBRLCompact(cc.receita)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[11px]">
                    <span className="text-zinc-500">Resultado</span>
                    <span className={`font-mono ${cc.resultado >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {fmtBRLCompact(cc.resultado)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <EntriesDrillDown
        open={!!drillType}
        onClose={() => setDrillType(null)}
        type={drillType}
        competence={competence}
        companyId={companyFilter}
      />
    </div>
  );
}
